/**
 * Copy for the knowledge base of a shared agent.
 *
 * Rules book (D1) belongs to the agent owner: a recipient can never change it.
 * Customer Data (D3) is each recipient's own selection, kept per user and never
 * the owner's folders — except on a shared-memory share, which runs on the
 * owner's folders for everyone.
 */
export const SHARED_KB_COPY = {
  // ── Agent screen, for a recipient ──
  dim1Locked: "Set by the agent owner. You can't change it.",
  ownerOnlyToggle: 'Only the agent owner can turn this on or off.',
  dim3Info: 'Choose your own folders. Only you search them. Other people choose their own.',
  dim3None: 'No folders chosen. The agent answers without your documents.',
  dim3Selected: (count) => `Your documents: ${count} folder${count === 1 ? '' : 's'}`,
  chooseFolders: 'Choose folders',
  changeFolders: 'Change',
  removeFolders: 'Remove',
  pickerTitle: 'Choose your Customer Data (D3) folders',
  sharedFolderTag: (owner) => `Shared with you by ${owner}`,
  folderNotUsable: "This folder can't be used here. Ask its owner to share the folder with you.",
  embeddingDiffers: "Uses a different embedding model than your other folders, so it can't be combined with them.",
  noFolders: 'You have no document folders to choose from. Ask for a folder to be shared with you.',
  unknownFolder: 'Folder',
  saved: 'Saved. This agent now searches your folders.',
  removed: 'Your folders were removed from this agent.',
  removeHelp: 'Remove your folders from this agent. It will stop searching your documents.',
  removeConfirm: (count, agentName) =>
    `Remove your ${count} folder${count === 1 ? '' : 's'} from ${agentName || 'this agent'}?`,
  ownerMode: "This agent uses the owner's documents for everyone. You can't choose your own.",
  dim3Disabled: "This agent doesn't search documents.",

  // ── Chat screen: `tool_warning` entries with tool_type "kb_folder", by reason ──
  warnings: {
    dim3_not_selected: 'This agent has none of your documents yet. Choose folders so it can search them.',
    folder_unavailable: 'A folder you chose was deleted. Choose another one.',
    share_revoked: 'A folder you chose is no longer shared with you.',
    owner_inactive: "A shared folder can't be searched because its owner is no longer active.",
  },
  warningFallback: "Some of this agent's documents could not be searched.",

  // ── Errors ──
  ownerOnlyChange: 'Only the agent owner can change this.',
  folderNotAllowed: 'You can only choose your own folders, or folders shared with you.',
  embeddingMismatch: "This folder can't be used with this agent. Choose a different folder.",
  saveFailed: 'Could not save your folders.',
  removeFailed: 'Could not remove your folders.',

  // ── Share dialog, for the person sharing ──
  shareDim3Note: 'Your Customer Data (D3) folders are not shared with this agent. Each person chooses their own.',
  shareDim3Hint: 'Want others to use one of your folders? Share that folder separately.',
  shareDim1Note: "Everyone who uses this agent searches these folders. They can't change them.",
};

/** Reasons a recipient can fix by picking folders again. */
export const KB_WARNING_REASONS_WITH_PICKER = ['dim3_not_selected', 'folder_unavailable', 'share_revoked'];

/**
 * Whether a `tool_warning` entry is about knowledge-base documents rather than
 * an app to reconnect. `dim3_not_selected` is matched on the reason alone; the
 * other reasons can also sit on tool entries, so they count only on a
 * `kb_folder` entry.
 */
export const isKbFolderWarning = (tool) =>
  tool?.tool_type === 'kb_folder' || tool?.reason === 'dim3_not_selected';
